/**
 * CRM 路由系统 — 基于 History API 的轻量级路由
 *
 * 本模块实现了 CRM 前端的客户端路由（client-side routing），不依赖任何第三方路由库。
 * 通过监听浏览器的 popstate 事件和手动调用 navigate() 方法来管理页面切换。
 *
 * 设计要点：
 * - 路由路径格式为 /crm/{page}，与 Vite 的 base 配置一致
 * - 登录态和权限通过共享 authStore 判断，兼容 HttpOnly Cookie 会话
 * - 路由变更通过自定义事件 'crm-navigate' 通知 CrmApp 组件重新渲染
 * - 未登录或无页面权限的访问会重定向到安全页面
 *
 * 主要导出：
 * - Route 接口：路由项的数据结构定义
 * - routes 数组：所有合法路由的注册表
 * - CrmRouter 类：路由核心逻辑
 * - router 单例：全局唯一的路由实例，供各组件引用
 */
import { authStore } from '@crm/shared/stores/auth.js';

/**
 * 路由项接口 — 描述一个页面路由的基本属性
 */
export interface Route {
  /** 路由路径标识，不含 /crm/ 前缀，如 'dashboard'、'customers' */
  path: string;
  /** 页面中文名称，用于导航栏显示 */
  label: string;
  /** 是否需要登录才能访问（当前所有业务页面均为 true） */
  requireAuth: boolean;
  /** 访问页面所需的后端模块权限码；未配置时只校验登录态 */
  permission?: string;
}

/**
 * 路由注册表 — 所有合法页面的路径定义
 * resolvePath() 会检查用户输入的 URL 是否在此列表中，不匹配则重定向到 'not-found'
 * 导航栏 (index.ts) 也引用此列表来生成菜单项
 */
export const routes: Route[] = [
  { path: 'dashboard', label: '数据概览', requireAuth: true },           // 首页仪表盘，展示关键统计指标
  { path: 'customers', label: '客户管理', requireAuth: true, permission: 'customer:manage' },
  { path: 'emails', label: '邮件管理', requireAuth: true, permission: 'email:manage' },
  { path: 'users', label: '用户管理', requireAuth: true, permission: 'user:manage' },
  { path: 'email-accounts', label: '邮箱账户', requireAuth: true, permission: 'email:manage' },
  { path: 'permissions', label: '权限管理', requireAuth: true, permission: 'permission:manage' },
  { path: 'customer-emails', label: '客户邮箱', requireAuth: true, permission: 'email:manage' },
  { path: 'email-templates', label: '邮件模板', requireAuth: true, permission: 'template:manage' },
  { path: 'email-tasks', label: '邮件任务', requireAuth: true, permission: 'task:manage' },
  { path: 'email-blacklist', label: '黑名单', requireAuth: true, permission: 'blacklist:manage' },
  { path: 'products', label: '产品管理', requireAuth: true },           // 产品信息维护
  { path: 'news', label: '新闻管理', requireAuth: true },               // 官网新闻内容管理
  { path: 'about', label: 'About 管理', requireAuth: true, permission: 'website:manage' }, // 官网 About 内容管理
  { path: 'contacts', label: '联系管理', requireAuth: true },           // 官网联系表单管理
  { path: 'resources', label: '资源库', requireAuth: true, permission: 'resource:manage' },
  { path: 'settings', label: '个人设置', requireAuth: true },           // 个人设置（API Token 管理等）
];

/** 登录页路径标识 */
const LOGIN_PATH = 'login';
/** 已登录用户的默认跳转路径 */
const DEFAULT_AUTH_PATH = 'dashboard';

/**
 * CRM 路由控制器
 *
 * 管理当前页面路径和登录状态，通过 History API 实现无刷新导航。
 * 导航时派发自定义事件 'crm-navigate'，由 CrmApp 组件监听并更新视图。
 *
 * 使用方式：
 * - 调用 router.navigate('customers') 导航到客户页
 * - 通过 router.currentPath 获取当前页面标识
 * - 通过 router.isLoggedIn 检查登录状态
 */
export class CrmRouter {
  /** 当前页面路径标识，如 'dashboard'、'customers'、'login' */
  private _currentPath: string;
  /** 当前是否已登录（初始化时从共享认证状态读取） */
  private _isLoggedIn: boolean;

  constructor() {
    this._isLoggedIn = authStore.state.isLoggedIn;
    // 根据当前 URL 解析出页面标识
    this._currentPath = this.resolvePath(window.location.pathname);

    // 监听浏览器前进/后退按钮，同步路由状态并触发视图更新
    window.addEventListener('popstate', () => {
      const requestedPath = this.resolvePath(window.location.pathname);
      const newPath = this.guardPath(requestedPath);
      if (newPath !== requestedPath) {
        window.history.replaceState({}, '', `/crm/${newPath}`);
      }
      if (newPath !== this._currentPath) {
        this._currentPath = newPath;
        this.dispatchNavigate();
      }
    });
  }

  /** 当前页面路径标识（只读） */
  get currentPath() { return this._currentPath; }
  /** 当前是否已登录（只读） */
  get isLoggedIn() { return this._isLoggedIn; }

  /**
   * 恢复浏览器 Cookie 会话、加载权限并校验首次访问路径。
   *
   * 登录页在没有缓存会话时不主动发请求；业务页面始终向后端确认会话，
   * 防止仅凭本地缓存渲染受保护页面。
   */
  async initialize(): Promise<void> {
    const requestedPath = this.resolvePath(window.location.pathname);
    if (requestedPath !== LOGIN_PATH || authStore.state.isLoggedIn) {
      await authStore.fetchUser();
    }
    this._isLoggedIn = authStore.state.isLoggedIn;
    const guardedPath = this.guardPath(requestedPath);
    this._currentPath = guardedPath;
    if (guardedPath !== requestedPath) {
      window.history.replaceState({}, '', `/crm/${guardedPath}`);
    }
    this.dispatchNavigate();
  }

  /**
   * 设置登录状态（由登录流程调用）
   *
   * 当用户登录成功后，如果当前停留在登录页则自动跳转到 dashboard；
   * 当用户登出后，如果当前不在登录页则自动跳转到登录页。
   *
   * @param loggedIn - true 表示已登录，false 表示已登出
   * @sideeffect 可能触发页面导航（调用 navigate）
   */
  setLoginState(loggedIn: boolean) {
    this._isLoggedIn = loggedIn;
    if (loggedIn && this._currentPath === LOGIN_PATH) {
      // 已登录且停留在登录页 → 跳转到默认首页
      this.navigate(DEFAULT_AUTH_PATH);
    } else if (!loggedIn && this._currentPath !== LOGIN_PATH && this._currentPath !== 'landing') {
      // 已登出且不在登录页 → 跳转到登录页（landing 页面除外）
      this.navigate(LOGIN_PATH);
    }
  }

  /**
   * 解析 URL 路径为页面标识
   *
   * 将浏览器 URL 中的 pathname 转换为内部页面标识。
   * 支持的 URL 格式：/crm/{page} 或 /crm/{page}?query=xxx
   * 如果 URL 不匹配 /crm/ 前缀或页面不在路由表中，则根据登录态返回默认页或 not-found。
   *
   * @param urlPath - 浏览器 URL 的 pathname 部分，如 '/crm/customers' 或 '/crm/login'
   * @returns 页面标识，如 'customers'、'login'、'dashboard'、'not-found'
   */
  private resolvePath(urlPath: string): string {
    // 匹配 /crm/ 前缀，捕获后续的页面路径部分
    const match = urlPath.match(/^\/crm\/?(\S*)$/);
    if (!match) return this._isLoggedIn ? DEFAULT_AUTH_PATH : LOGIN_PATH;

    let page = match[1] || '';
    if (!page) return this._isLoggedIn ? DEFAULT_AUTH_PATH : LOGIN_PATH;

    // 去除查询参数部分（如 ?page=1），只保留路径
    const qIndex = page.indexOf('?');
    if (qIndex >= 0) page = page.substring(0, qIndex);

    // 登录页特殊处理：始终允许访问
    if (page === LOGIN_PATH) return LOGIN_PATH;

    // 检查是否为路由表中的合法路径，不合法则返回 not-found
    const validPaths = routes.map(r => r.path);
    return validPaths.includes(page) ? page : 'not-found';
  }

  /**
   * 按登录态和模块权限校验页面，拒绝时返回登录页或默认业务页。
   *
   * @param page - 已通过合法路径检查的页面标识
   * @returns 允许访问的页面标识
   */
  private guardPath(page: string): string {
    if (page === LOGIN_PATH) return this._isLoggedIn ? DEFAULT_AUTH_PATH : LOGIN_PATH;
    if (page === 'not-found') return this._isLoggedIn ? page : LOGIN_PATH;

    const route = routes.find((item) => item.path === page);
    if (route?.requireAuth && !this._isLoggedIn) return LOGIN_PATH;
    if (route?.permission && !authStore.hasPermission(route.permission)) return DEFAULT_AUTH_PATH;
    return page;
  }

  /**
   * 导航到指定页面
   *
   * 更新浏览器地址栏 URL（pushState），并派发导航事件通知视图更新。
   * 登录页的 URL 为 /crm/login，其他页面的 URL 为 /crm/{path}。
   * path 支持携带 query（如 'emails?email_id=123'）：_currentPath 只保存
   * 剥离 query 后的页面标识（保证页面匹配和导航高亮正确），地址栏保留完整 query。
   *
   * @param path - 目标页面标识（可带 query），如 'dashboard'、'emails?email_id=1'、'login'
   * @sideeffect 修改浏览器地址栏、派发 'crm-navigate' 自定义事件
   */
  navigate(path: string) {
    // 剥离 query 部分作为页面标识，地址栏 URL 保留完整 query
    const qIndex = path.indexOf('?');
    const page = qIndex >= 0 ? path.substring(0, qIndex) : path;
    const guardedPage = this.guardPath(page);
    const allowedPath = guardedPage === page ? path : guardedPage;
    const target = guardedPage === LOGIN_PATH ? '/crm/login' : `/crm/${allowedPath}`;
    this._currentPath = guardedPage;
    // 使用 History API 更新地址栏，不触发页面刷新
    window.history.pushState({}, '', target);
    this.dispatchNavigate();
  }

  /**
   * 派发 'crm-navigate' 自定义事件
   *
   * CrmApp 组件在 connectedCallback 中监听此事件，
   * 收到后更新 currentPage 状态并触发 Lit 重新渲染。
   */
  private dispatchNavigate() {
    window.dispatchEvent(new CustomEvent('crm-navigate', {
      detail: { path: this._currentPath }
    }));
  }
}

/**
 * 全局路由单例
 *
 * 应用启动时创建，在整个应用生命周期中共享。
 * 各组件通过 import { router } 引用此实例进行导航和状态查询。
 */
export const router = new CrmRouter();
